import Keyline from 'components/shared/KeyLine';
import ResponsiveMedia from 'components/shared/ResponsiveMedia/ResponsiveMedia';
import { FeaturesProps } from 'libs/contentful/props';
import Marquee from 'react-fast-marquee';
import { BackgroundColor, EnterpriseBackgroundColor, KeyLinePosition } from 'types/constants';
import { cn, getColorway, getColorwayWithTransparency } from 'utils/styles';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { LayoutOrientation } from 'types/constants';

const darkColorways = [
  BackgroundColor.DarkGreenGradient,
  BackgroundColor.DarkGreen,
  EnterpriseBackgroundColor.DarkGreen,
];

const Features = ({
  sys,
  title,
  color,
  transparentBackground = false,
  fullWidth = false,
  mobileOrientation = LayoutOrientation.Vertical, // DEFAULT: Vertical (non-scrolling grid)
  keyline,
  content,
}: FeaturesProps) => {
  if (!content || !content.items.length) return null;
  const marqueeItems = content.items.map(media => (
    <ResponsiveMedia
      key={media.sys?.id}
      {...media}
      className={cn('max-w-[225px] shrink-0 inline-flex items-center text-center align-middle justify-center', {
        'in-cw-dark-colorways:white-images': transparentBackground, // apply white-images if transparent and page background is dark
        'white-images': !transparentBackground && darkColorways.includes(getColorway(color)), // apply white-images if colorway is dark
      })}
      testId="featuresLogo"
    />
  ));

  const desktopShouldMarquee = marqueeItems.length >= 5;
  const mobileShouldMarquee = marqueeItems.length >= 3;

  return (
    <section className={getColorwayWithTransparency(color, transparentBackground)}>
      <div className={!fullWidth ? 'content-wrapper' : ''}>
        {keyline === KeyLinePosition.Top && <Keyline testId="featuresTopKeyline" />}
        <div className="py-8">
          <div className="flex flex-col gap-6 md:gap-0 sm:flex-row items-stretch justfiy-between font-sans text-body-saans font-semibold relative sm:overflow-hidden min-h-9">
            {title && (
              <div
                className={cn(
                  { 'sm:w-2/12': !desktopShouldMarquee, 'sm:w-fit': desktopShouldMarquee },
                  'flex items-center gap-4 w-full',
                )}>
                <p data-testid="featuresTitle" className="min-w-fit text-nowrap" {...liveUpdateAttrs(sys?.id, 'title')}>
                  {title}
                </p>
                <div
                  className={cn('w-full h-[2px] sm:w-[2px] sm:h-full sm:min-w-[2px] bg-kale', {
                    'sm:mx-auto': !desktopShouldMarquee,
                    'in-cw-dark-colorways:bg-white': transparentBackground,
                    'bg-white': darkColorways.includes(getColorway(color)),
                  })}
                />
              </div>
            )}

            {/* Desktop behavior: marquee if >4, else static */}
            {desktopShouldMarquee ? (
              <Marquee autoFill speed={40} gradient={false} className="[img]:mx-10 !hidden sm:!flex">
                {marqueeItems.map(item => (
                  <div data-testid="featuresMarquee" className="mx-10" key={item.key as string}>
                    {item}
                  </div>
                ))}
              </Marquee>
            ) : (
              <div
                data-testid="featuresStatic"
                className={cn(
                  'hidden sm:flex flex-1 items-center flex-wrap gap-4',
                  fullWidth ? 'justify-between sm:pr-16' : 'justify-evenly',
                )}>
                {marqueeItems}
              </div>
            )}

            {/* MOBILE */}
            {mobileOrientation === LayoutOrientation.Horizontal ? (
              mobileShouldMarquee ? (
                <Marquee autoFill speed={40} gradient={false} pauseOnHover={false} className="sm:!hidden [img]:mx-6">
                  {marqueeItems.map(item => (
                    <div
                      key={item.key as string}
                      className="mx-6 pointer-events-none select-none"
                      tabIndex={-1}
                      aria-hidden="true"
                      data-testid="featuresMarqueeMobile">
                      {item}
                    </div>
                  ))}
                </Marquee>
              ) : (
                <div
                  className="sm:hidden flex flex-row justify-evenly items-center gap-4 w-full"
                  data-testid="featuresRowMobile">
                  {marqueeItems.map(item => (
                    <div
                      key={item.key as string}
                      className="pointer-events-none select-none"
                      tabIndex={-1}
                      aria-hidden="true">
                      {item}
                    </div>
                  ))}
                </div>
              )
            ) : (
              <div className={cn('grid grid-cols-2 xs:grid-cols-3 sm:hidden gap-4 flex-1')}>{marqueeItems}</div>
            )}
          </div>
        </div>
        {keyline === KeyLinePosition.Bottom && <Keyline testId="featuresBottomKeyline" />}
      </div>
    </section>
  );
};

export default Features;
